/**
 * Pure model behind the owner's invite form and member access editor (P-1.3).
 *
 * Effective access is entitlements ∩ (role ∪ inline), resolved and enforced by core. This
 * module only decides what the editor shows and what it sends.
 */

import type { CategoryEntitlement } from "../../context/auth-me";
import { AGENT_SCOPE_ACTIONS, type AgentScopeAction } from "../../context/auth-me";
import { readAxiosV2Error } from "../../lib/api-v2/errors";

export type AssignableRole = "member" | "viewer" | "supervisor";

/** "owner" is granted only through the ownership protocol, never by assignment. */
export const ASSIGNABLE_ROLE_OPTIONS: { value: AssignableRole; label: string }[] = [
  { value: "member", label: "Member" },
  { value: "viewer", label: "Viewer" },
  { value: "supervisor", label: "Supervisor" },
];

const VIEWER = [
  "agents.view", "workflows.view", "calls.history", "calls.recordings.view", "schedules.view",
  "callbacks.view", "contacts.view", "analytics.view", "campaigns.view",
];

// Mirrors core app/domain/rbac/roles.py ROLE_PERMISSIONS. Display only: the server resolves.
const ROLE_IMPLIED: Record<AssignableRole, readonly string[]> = {
  member: [],
  viewer: VIEWER,
  supervisor: [
    ...VIEWER,
    "supervision.view", "supervision.guide", "supervision.listen", "supervision.takeover", "supervision.triage",
  ],
};

export function isAssignableRole(role: unknown): role is AssignableRole {
  return role === "member" || role === "viewer" || role === "supervisor";
}

export function impliedPermissions(role: string | null | undefined): readonly string[] {
  return isAssignableRole(role) ? ROLE_IMPLIED[role] : [];
}

/**
 * Whether the organization holds this category at all. Only an explicit
 * `feature_enabled: false` denies: core falls back to its schema default for an absent
 * key, and guessing that default here would duplicate it.
 */
export function isCategoryEntitled(
  entitlements: Record<string, CategoryEntitlement> | null | undefined,
  categoryId: string,
): boolean {
  return entitlements?.[categoryId]?.feature_enabled !== false;
}

// ── Agent scope ─────────────────────────────────────────────────────────

export type ScopeMode = "all" | "selected" | "none";
export interface ActionGrantDraft { mode: ScopeMode; ids: string[]; folders: string[] }
export type ScopeDraft = Record<AgentScopeAction, ActionGrantDraft>;

export type StoredGrant = "ALL" | { ids: string[]; folders: string[] };
export interface StoredScope { v: 1; agents: Partial<Record<AgentScopeAction, StoredGrant>> }

export const SCOPE_ACTION_LABELS: Record<AgentScopeAction, string> = {
  view: "View",
  edit: "Edit",
  delete: "Delete",
  execute: "Run calls",
  listen: "Listen live",
  takeover: "Take over",
};

const draftOf = (mode: ScopeMode): ScopeDraft => {
  const draft = {} as ScopeDraft;
  for (const action of AGENT_SCOPE_ACTIONS) draft[action] = { mode, ids: [], folders: [] };
  return draft;
};

export const FULL_SCOPE_DRAFT: ScopeDraft = draftOf("all");

/**
 * Stored scope → editor state. A missing action is denied, and so is a missing scope:
 * core resolves an unset member scope to no agents once member_scope enforces.
 */
export function scopeToDraft(stored: unknown): ScopeDraft {
  const agents = (stored as StoredScope | null)?.agents;
  const draft = draftOf("none");
  if (!agents || typeof agents !== "object") return draft;
  for (const action of AGENT_SCOPE_ACTIONS) {
    const grant = agents[action];
    if (grant === "ALL") draft[action] = { mode: "all", ids: [], folders: [] };
    else if (grant && typeof grant === "object") {
      draft[action] = {
        mode: "selected",
        ids: Array.isArray(grant.ids) ? grant.ids.map(String) : [],
        folders: Array.isArray(grant.folders) ? grant.folders.map(String) : [],
      };
    }
  }
  return draft;
}

/** Editor state → the stored shape core validates. "none" actions are omitted (denied). */
export function draftToScope(draft: ScopeDraft): StoredScope {
  const agents: StoredScope["agents"] = {};
  for (const action of AGENT_SCOPE_ACTIONS) {
    const grant = draft[action];
    if (grant.mode === "all") agents[action] = "ALL";
    else if (grant.mode === "selected") agents[action] = { ids: [...grant.ids].sort(), folders: [...grant.folders].sort() };
  }
  return { v: 1, agents };
}

// ── Owner-side answers ───────────────────────────────────────────────────

export const DELIVERY_LABELS: Record<string, string> = {
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  failed: "Delivery failed",
  link_only: "Link only",
};

const OWNER_ERRORS: Record<string, string> = {
  invalid_access: "Some of the selected access is not valid for this organization.",
  already_member: "That person is already a member of this organization.",
  suspended_member: "That person is a suspended member. Reactivate them instead of inviting.",
  invitation_pending: "An invitation is already pending for that address.",
  already_accepted: "This invitation was already accepted. Remove the member instead.",
  feature_not_enabled: "This is not enabled for your organization yet.",
};

export function ownerErrorMessage(error: unknown): string {
  const { code, message } = readAxiosV2Error(error);
  return (code && OWNER_ERRORS[code]) || message || "Please try again.";
}

export function ownerErrorCode(error: unknown): string | null {
  return readAxiosV2Error(error).code;
}

export function inviteLink(invite: { accept_token?: string | null; accept_url?: string | null }): string | null {
  if (invite.accept_url) return invite.accept_url;
  if (!invite.accept_token) return null;
  return `${window.location.origin}/invite/accept#token=${encodeURIComponent(invite.accept_token)}`;
}
